/**
 * `/itineraries/:slug/edit` — loads the itinerary via `trpc.itinerary.getBySlug`
 * and mounts the editor once data is present. Handles loading / error /
 * not-owner states (non-owner edit is server-gated; we surface a clear
 * message rather than a raw error).
 */

import { useParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Heading1, BodyText } from '../components/ui/Typography';
import { ItineraryEditor } from '../components/itinerary/ItineraryEditor';
import { useAuth } from '../hooks/useAuth';

export const ItineraryEditPage = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const query = trpc.itinerary.getBySlug.useQuery(
    { slug },
    { enabled: slug.length > 0 }
  );

  if (query.isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer className="mt-2">
          <BodyText className="text-gray-500">Loading itinerary…</BodyText>
        </PageContainer>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer className="mt-2">
          <Heading1>Itinerary not found</Heading1>
          <BodyText className="mt-2 text-gray-600">
            This itinerary doesn&apos;t exist or you don&apos;t have access to
            it.
          </BodyText>
        </PageContainer>
      </div>
    );
  }

  const it = query.data;
  const isOwner = Boolean(user && user.id === it.ownerId);

  if (!isOwner) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer className="mt-2">
          <Heading1>{it.title}</Heading1>
          <BodyText className="mt-2 text-gray-600">
            You can only edit your own itineraries.
          </BodyText>
        </PageContainer>
      </div>
    );
  }

  return <ItineraryEditor slug={slug} initial={it} />;
};
